import {
  black,
  gray01,
  gray04,
  gray05,
  primary,
  white,
} from 'presentations/views/styles/color';
import {
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import styled from '@emotion/styled';
import Image, {
  getImageUrlWithLocale,
} from 'presentations/views/components/atoms/Image';
import Icon from 'presentations/views/components/atoms/Icon';
import { ReactNode, SyntheticEvent, useState } from 'react';
import Tabs from 'presentations/views/components/atoms/Tabs';
import Transition from 'presentations/views/components/atoms/Transition';
import { useRouter } from 'next/router';
import Accordion from 'presentations/views/components/atoms/Accordion';
import { imagePath } from 'presentations/assets';

interface ImageItem {
  title: string;
  description: string;
  imageUrl: string;
  imageWidth?: number;
  imageHeight?: number;
  isVideo?: boolean;
}

interface Props {
  items: ImageItem[];
  title: string;
  titleIcon?: ReactNode;
  subTitle?: string;
  description: string | ReactNode;
  link?: string;
  linkTitle?: string;
  imageWidth?: number;
  imageHeight?: number;
}

export default function Component(props: Props) {
  const isMobile = isMediaMax('tablet');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [activeIndexText, setActiveIndexText] = useState<number | null>(null);
  const isDesktop = isMediaMax('desktop');
  const tabs =
    props.items.length > 1 ? props.items.map((d) => ({ label: d.title })) : [];
  const router = useRouter();

  const onClick = (e: SyntheticEvent<HTMLDivElement>) => {
    const i = Number(e.currentTarget.dataset.id || 0);
    setActiveIndex(i);
    if (activeIndex === i) {
      setActiveIndexText(activeIndexText === null ? i : null);
      return;
    }
    setActiveIndexText(i);
  };

  const onChangeTab = (event: React.ChangeEvent<{}>, i: number) => {
    setActiveIndex(i);
    setActiveIndexText(i);
  };

  return (
    <Content>
      <MainContent>
        <TitleContainer>
          <Title>
            {props.titleIcon}
            {props.title}
          </Title>
          {props.subTitle && <SubTitle>{props.subTitle}</SubTitle>}
          <Description>{props.description}</Description>
        </TitleContainer>

        {isDesktop && tabs.length > 0 ? (
          <NavigationContainer>
            <Tabs
              indicatorColor="primary"
              value={activeIndex}
              onChange={onChangeTab}
              tabs={tabs}
              scrollButtons="auto"
            />
          </NavigationContainer>
        ) : (
          <ImageMenuList>
            {props.items.length > 1 &&
              props.items.map((item, i) => (
                <ImageMenuListItem data-id={i} onClick={onClick} key={i}>
                  <Accordion
                    expanded={activeIndexText === i}
                    onChange={(event: any, expanded: boolean) =>
                      setActiveIndex(i)
                    }
                    summary={
                      <ImageMenuListItemTitleContainer>
                        <ImageMenuListItemTitle>
                          {item.title}
                        </ImageMenuListItemTitle>
                        <ImageMenuListItemIcon
                          name={`chevron-right`}
                          isActive={activeIndexText === i}
                        />
                      </ImageMenuListItemTitleContainer>
                    }
                    hiddenExpandIcon
                    details={
                      <ImageMenuListItemText>
                        {item.description}
                      </ImageMenuListItemText>
                    }
                  />
                </ImageMenuListItem>
              ))}
          </ImageMenuList>
        )}
      </MainContent>
      <ImageContent>
        {props.items.map((item, i) => (
          <Transition key={i} in={i === activeIndex} name="fade">
            <div style={{ display: i === activeIndex ? 'block' : 'none' }}>
              {item.isVideo ? (
                <MainVideo
                  src={imagePath(
                    getImageUrlWithLocale(item.imageUrl, router.locale!),
                  )}
                  width={props.imageWidth}
                  height={props.imageHeight}
                  autoPlay
                  loop
                  muted
                  playsInline={isMobile}
                />
              ) : (
                <MainImage
                  src={item.imageUrl}
                  width={props.imageWidth}
                  height={props.imageHeight}
                  alt={item.title}
                  dpr={2}
                  locale={router.locale}
                  loading="auto"
                />
              )}
              {isDesktop && <ImageCaption>{item.description}</ImageCaption>}
            </div>
          </Transition>
        ))}
      </ImageContent>
    </Content>
  );
}
const Content = styled.div`
  width: 100%;
  display: flex;
  color: ${black};
  justify-content: space-between;
  ${mediaMax.desktop} {
    display: block;
  }
`;
const MainImage = styled(Image)`
  width: 100%;
  max-width: 797px;
  height: auto;
  z-index: 2;
`;
const MainVideo = styled.video`
  width: 100%;
  max-width: 797px;
  height: auto;
  z-index: 2;
`;
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 290px;
  ${mediaMax.desktop} {
    width: 100%;
    justify-content: center;
  }
`;
const ImageContent = styled.div`
  flex: 1;
  margin-left: ${spacing(24)};
  ${mediaMax.desktop} {
    margin-left: 0;
    text-align: center;
  }
`;
const TitleContainer = styled.div``;
const Title = styled.h2`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(32)};
  line-height: 1.2;
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(4)};
  ${mediaMax.desktop} {
    justify-content: center;
  }
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
  }
`;
const SubTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  color: ${black};
  ${mediaMax.desktop} {
    text-align: center;
  }
  ${mediaMax.tablet} {
    font-size: ${fontSize(16)};
  }
`;
const Description = styled.div`
  margin-top: ${spacing(16)};
  ${mediaMax.desktop} {
    text-align: center;
  }
  ${mediaMax.tablet} {
    margin-top: ${spacing(8)};
  }
`;

const ImageMenuList = styled.div`
  margin-top: ${spacing(16)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(8)};
  }
`;

const ImageMenuListItem = styled.div<{ isActive?: boolean }>`
  display: flex;
  justify-content: space-between;
  border-left-width: 2px;
  border-left-color: ${(props) => (props.isActive ? primary : 'transparent')};
  font-size: ${fontSize(15)};
  padding: ${spacing(8)} 0;
  border-bottom: 1px solid ${black};
  cursor: pointer;
  :first-of-type {
    border-top: 1px solid ${black};
  }
`;
const ImageMenuListItemTitleContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${fontWeight('bold')};
`;
const ImageMenuListItemTitle = styled.div`
  flex: 1;
  color: ${black};
`;
const ImageMenuListItemActive = styled.div<{ isActive: boolean }>`
  width: 5px;
  height: 24px;
  background-color: ${black};
  border-radius: ${borderRadius(6)};
  margin-right: ${spacing(8)};
  visibility: ${(props) => (props.isActive ? 'visible' : 'hidden')};
`;
const ImageMenuListItemText = styled.div`
  margin-top: ${spacing(8)};
  padding-left: 13px;
  font-size: ${fontSize(14)};
  color: ${black};
`;
const ImageMenuListItemIcon = styled(Icon)<{ isActive: boolean }>`
  color: ${gray01};
  animation: ${(props) =>
    props.isActive ? '0.2s linear 1 forward' : '0.1s linear 1 back'};
  transform: ${(props) => (props.isActive ? 'rotate(90deg)' : 'rotate(0)')};

  @keyframes forward {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(90deg);
    }
  }
  @keyframes back {
    0% {
      transform: rotate(90deg);
    }
    100% {
      transform: rotate(0);
    }
  }
`;

const NavigationContainer = styled.div`
  max-width: 797px;
  margin: 0 auto;
  border-bottom: 1px solid ${gray04};
  display: flex;
  justify-content: center;
  margin-top: ${spacing(32)};
  margin-bottom: ${spacing(24)};
  ${mediaMax.tablet} {
    width: 100%;
    margin-top: ${spacing(16)};
  }
`;

const ImageCaption = styled.p`
  margin-top: ${spacing(8)};
  ${mediaMax.tablet} {
    text-align: center;
  }
`;
